<template>
  <div class="home">
    <section class="home-hero" aria-labelledby="home-title">
      <span class="home-badge"><span class="home-badge-free">FREE</span>Form score by Exatom</span>
      <h1 id="home-title" class="home-title">How good is your form?<br> Find out in 30 seconds.</h1>
      <p class="home-intro">
        Paste the address of any form. We run {{ checkCount }} checks on accessibility, usability, validation and more, and tell you what to fix first.
      </p>
      <url-box
        ref="heroBox"
        class="home-url-box"
        input-id="hero-url"
        on-dark
        :value="value"
        :loading="loading"
        :error="error"
        @input="$emit('input', $event)"
        @submit="$emit('score')"
      />
      <p v-if="couldNotScore" class="home-help">
        Some forms only load after a click or a login. Our
        <a :href="extensionUrl" target="_blank" rel="noopener">Chrome extension</a>
        scores the form on the page you have open, so it handles those too. Still stuck?
        <a :href="supportHref">Let us know</a> and we'll look into it.
      </p>
      <ul class="home-trust">
        <li v-for="item in trustItems" :key="item"><ph-icon name="check" :size="16" />{{ item }}</li>
      </ul>
      <div class="home-logos">
        <p>Trusted on high-value forms at</p>
        <ul>
          <li v-for="client in clientLogos" :key="client.file">
            <img :src="`/assets/clients/${client.file}`" :alt="client.name" :width="client.width" :height="client.height" decoding="async" />
          </li>
        </ul>
      </div>
    </section>

    <section id="how-it-works" class="home-section home-report" aria-labelledby="home-report-title">
      <div>
        <p class="home-eyebrow">Your report</p>
        <h2 id="home-report-title" class="home-heading">Know where you lose people.<br> And what to fix first.</h2>
        <p class="home-text">
          Your score shows straight away which parts of your form slow visitors down. The full report turns that into a short list of fixes,
          the ones that win back the most completions at the top.
        </p>
        <button type="button" class="ex-btn ex-btn--primary home-section-action" @click="focusHero">
          <ph-icon name="arrow-right" :size="18" />
          <span class="ex-btn__label" data-label="Score my form">Score my form</span>
        </button>
      </div>
      <figure class="home-sample" aria-label="Sample report for example.com/contact">
        <div class="home-sample-head">
          <score-ring :score="7.1" grade="Fair" :size="140" :stroke="12" dark />
          <figcaption>
            <span class="home-sample-label">Sample report</span>
            <span class="home-sample-page">example.com/contact</span>
          </figcaption>
        </div>
        <ul class="home-sample-bars">
          <li v-for="bar in sampleBars" :key="bar.key">
            <ph-icon :name="bar.icon" :size="20" class="home-sample-icon" />
            <span class="home-sample-name">{{ bar.name }}</span>
            <span class="home-sample-track"><span :style="{ width: `${bar.score * 10}%`, background: `var(--ex-score-${bar.level})` }" /></span>
            <span class="home-sample-score">{{ bar.score }}<span> /10</span></span>
          </li>
        </ul>
      </figure>
    </section>

    <section id="what-we-check" class="home-section home-checks" aria-labelledby="home-checks-title">
      <div>
        <p class="home-eyebrow">What we check</p>
        <h2 id="home-checks-title" class="home-heading home-heading--small">{{ categoryCountWord }} categories.<br> {{ checkCount }} checks.</h2>
        <p class="home-text home-text--small">The same audit our CRO experts run before they touch a form. Weighted by what moves conversion.</p>
      </div>
      <ul class="home-category-grid">
        <li v-for="category in categories" :key="category.key" class="home-category">
          <ph-icon :name="category.icon" :size="24" class="home-category-icon" />
          <h3>{{ categoryLabel(category.key) }}</h3>
          <p>{{ category.description }}</p>
        </li>
      </ul>
    </section>

    <section class="home-section home-reality" aria-labelledby="home-reality-title">
      <div class="home-reality-head">
        <p class="home-eyebrow">Score versus reality</p>
        <h2 id="home-reality-title" class="home-heading">Your score says what could go wrong.<br> Your visitors say what does.</h2>
      </div>
      <div class="home-reality-cards">
        <div class="home-code-card">
          <span class="home-card-eyebrow">Form score · free, no sign-up</span>
          <h3 class="home-card-title">What the code tells us</h3>
          <p class="home-code-text">
            A form can tick every best practice and still lose half its visitors. The score tells you what is likely to go wrong.
            Measuring real sessions shows you what does go wrong, field by field, and what it costs you.
            <strong>Start with the score, then let your visitors have the final word.</strong>
          </p>
          <ul class="home-code-list">
            <li v-for="item in codeExamples" :key="item.name">
              <ph-icon name="check" :size="16" class="home-code-icon" />
              <span>{{ item.name }}</span>
              <span class="home-code-category">{{ item.category }}</span>
            </li>
          </ul>
          <button type="button" class="ex-btn ex-btn--secondary ex-btn--medium home-section-action" @click="focusHero">
            <ph-icon name="arrow-right" :size="18" />
            <span class="ex-btn__label" data-label="Score my form">Score my form</span>
          </button>
        </div>
        <span class="home-reality-bridge" aria-hidden="true"><ph-icon name="arrow-right" :size="20" /></span>
        <div class="home-visitors-card">
          <span class="home-card-eyebrow home-card-eyebrow--mint">Free plan · €0 forever</span>
          <h3 class="home-card-title">What your visitors tell us</h3>
          <dl class="home-visitor-figures">
            <div><dt>Start rate</dt><dd>64%</dd></div>
            <div><dt>Completion</dt><dd>38%</dd></div>
            <div><dt>Missed value</dt><dd class="home-visitor-figure--mint">€12,400</dd></div>
          </dl>
          <ul class="home-benefits">
            <li v-for="benefit in visitorBenefits" :key="benefit"><ph-icon name="check" :size="16" />{{ benefit }}</li>
          </ul>
          <div class="home-visitors-actions">
            <a :href="signupUrl" target="_blank" rel="noopener" class="ex-btn ex-btn--light ex-btn--medium">
              <span class="ex-btn__label" data-label="Create free account">Create free account</span>
            </a>
            <span>No credit card</span>
          </div>
        </div>
      </div>
    </section>

    <div id="chrome-extension" class="home-section home-chrome">
      <chrome-extension-card wide />
    </div>

    <section class="home-section home-faq" aria-labelledby="home-faq-title">
      <div>
        <p class="home-eyebrow">Questions</p>
        <h2 id="home-faq-title" class="home-heading home-heading--faq">Good to know.</h2>
      </div>
      <ul class="home-faq-list">
        <li v-for="(item, index) in faq" :key="item.question">
          <h3>
            <button
              type="button"
              class="home-faq-toggle"
              :aria-expanded="openFaq === index ? 'true' : 'false'"
              :aria-controls="`faq-${index}`"
              @click="openFaq = openFaq === index ? null : index"
            >
              {{ item.question }}
              <ph-icon :name="openFaq === index ? 'minus' : 'plus'" :size="20" />
            </button>
          </h3>
          <p v-show="openFaq === index" :id="`faq-${index}`" class="home-faq-answer">{{ item.answer }}</p>
        </li>
      </ul>
    </section>

    <section class="home-final" aria-labelledby="home-final-title">
      <h2 id="home-final-title" class="home-final-title">Your form is one URL away<br> from a better score.</h2>
      <url-box
        class="home-url-box home-url-box--final"
        input-id="final-url"
        on-dark
        :value="value"
        :loading="loading"
        :error="error"
        @input="$emit('input', $event)"
        @submit="$emit('score')"
      />
    </section>
  </div>
</template>

<script>
import ChromeExtensionCard from './ChromeExtensionCard.vue'
import PhIcon from './PhIcon.vue'
import ScoreRing from './ScoreRing.vue'
import UrlBox from './UrlBox.vue'
import { AUDIT_CHECK_COUNT, CATEGORIES, countWord, getCategoryLabel, getScoreLevel, toSentenceCase } from '../score/report.mjs'
import { CHROME_EXTENSION_URL, SIGNUP_URL } from '../score/links'

export default {
  name: 'HomePage',
  components: { ChromeExtensionCard, PhIcon, ScoreRing, UrlBox },
  props: {
    value: { type: String, default: '' },
    loading: { type: Boolean, default: false },
    error: { type: String, default: '' },
    couldNotScore: { type: Boolean, default: false },
    supportHref: { type: String, required: true }
  },
  data () {
    return {
      checkCount: AUDIT_CHECK_COUNT,
      categories: CATEGORIES,
      extensionUrl: CHROME_EXTENSION_URL,
      signupUrl: SIGNUP_URL,
      openFaq: null,
      // Sizes as on exatom.io, which balances the logos optically.
      clientLogos: [
        { name: 'Randstad', file: 'randstad-logo.svg', width: 120, height: 18 },
        { name: 'Kia', file: 'kia-logo-black.svg', width: 65, height: 15 },
        { name: 'Hyundai', file: 'hyundai-logo-black.svg', width: 55, height: 26 },
        { name: 'DELTA Fiber', file: 'delta-fiber-logo-black.svg', width: 48, height: 26 },
        { name: 'INNO', file: 'inno-logo-black.svg', width: 49, height: 20 },
        { name: 'Brooks Running', file: 'brooks-logo-black.svg', width: 105, height: 13 }
      ],
      sampleScores: { Accessibility: 5.5, Usability: 6.5, Validation: 8, Style: 7.4, 'Web Standards': 8, Performance: 8 },
      codeExamples: [
        { name: 'Autofill compatibility', category: 'Usability' },
        { name: 'Agentic completion', category: 'Usability' },
        { name: 'Cross-device compatibility', category: 'Web standards' },
        { name: 'CTA and labels', category: 'Style' }
      ],
      visitorBenefits: [
        'See how many visitors start and finish your form',
        'Know how much conversion your form is missing',
        'Segment by device, browser and campaign',
        'Autofill and error insights, plus the full form audit',
        'Let Eureka tell you what to fix first'
      ],
      faq: [
        {
          question: 'Is it really free?',
          answer: "Yes. Scoring a form and reading the full report cost nothing, and you don't need an account."
        },
        {
          question: 'What do you do with my email?',
          answer: 'We use it to send you the link to your report.'
        },
        {
          question: 'My form is behind a login. Can you score it?',
          answer: 'Not from this page. Use the Chrome extension: it scores any form you can open in your browser, including ones behind a login or deep in a checkout.'
        },
        {
          question: 'How is the score different from Exatom form analytics?',
          answer: 'The score checks your form\'s code and layout at one moment. Form analytics shows what real visitors do: who starts, who gives up and what that costs you. You can try it for free on one form.'
        }
      ]
    }
  },
  computed: {
    trustItems () {
      return ['No sign-up needed', 'Results in seconds', `${this.checkCount} checks, ${this.categories.length} categories`]
    },
    categoryCountWord () {
      return toSentenceCase(countWord(this.categories.length))
    },
    sampleBars () {
      return this.categories
        .filter((category) => category.key in this.sampleScores)
        .map((category) => ({
          key: category.key,
          icon: category.icon,
          name: getCategoryLabel(category.key),
          score: this.sampleScores[category.key],
          level: getScoreLevel(this.sampleScores[category.key])
        }))
    }
  },
  methods: {
    categoryLabel: getCategoryLabel,
    focusHero () {
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
      this.$refs.heroBox.$el.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' })
      this.$refs.heroBox.focus()
    }
  }
}
</script>

<style scoped>
.home-hero {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 96px var(--ex-page-gutter) 72px;
  background: var(--ex-color-dark);
  text-align: center;
}

.home-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 30px;
  /* The FREE pill is 20px high, so 5px on the left matches the space above and below it. */
  padding: 0 14px 0 5px;
  border-radius: var(--ex-radius-pill);
  background: var(--ex-color-on-dark-subtle);
  color: var(--ex-color-white);
  font-size: 13px;
  font-weight: 600;
}

.home-badge-free {
  display: inline-flex;
  align-items: center;
  height: 20px;
  padding: 0 8px;
  border-radius: var(--ex-radius-pill);
  background: var(--ex-color-blue);
  font-size: 11px;
}

.home-title {
  max-width: 980px;
  margin-top: 28px;
  font-size: 76px;
  letter-spacing: -0.045em;
  line-height: 1.02;
  color: var(--ex-color-white);
}

.home-intro {
  max-width: 700px;
  margin-top: 24px;
  font-size: 20px;
  line-height: 1.55;
  color: var(--ex-color-neutral-100);
}

.home-url-box {
  margin-top: 40px;
}

.home-url-box--final {
  width: 640px;
  margin-top: 36px;
}

.home-help {
  max-width: 640px;
  font-size: 14px;
  line-height: 1.5;
  color: var(--ex-color-neutral-100);
}

.home-help a {
  color: var(--ex-color-white);
}

.home-trust {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 8px 24px;
  margin: 8px 0 0;
  padding: 0;
  list-style: none;
  font-size: 14px;
  color: var(--ex-color-neutral-100);
}

.home-trust li {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.home-trust .ph-icon {
  color: var(--ex-color-mint);
}

.home-logos {
  width: 100%;
  margin-top: 72px;
}

.home-logos p {
  margin-bottom: 18px;
  font-size: 13px;
  color: var(--ex-color-neutral-300);
}

.home-logos ul {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  gap: 24px 40px;
  margin: 0;
  padding: 0;
  list-style: none;
}

/* The logo files are dark or coloured; on the dark hero they show white at 55%, as on exatom.io. */
.home-logos img {
  display: block;
  filter: brightness(0) invert(1);
  opacity: 0.55;
}

.home-section {
  padding: 96px var(--ex-page-gutter);
}

.home-eyebrow {
  margin-bottom: 14px;
  font-size: 14px;
  font-weight: 600;
  color: var(--ex-color-blue);
}

.home-heading {
  font-size: 48px;
  letter-spacing: -0.035em;
  line-height: 1.08;
}

.home-heading--small {
  font-size: 44px;
}

.home-heading--faq {
  font-size: 40px;
}

.home-text {
  margin-top: 16px;
  font-size: 18px;
  line-height: 1.55;
  color: var(--ex-color-neutral-700);
}

.home-text--small {
  font-size: 17px;
}

.home-section-action {
  margin-top: 28px;
}

.home-report {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 64px;
  align-items: center;
  background: var(--ex-color-white);
}

.home-sample {
  margin: 0;
  padding: 36px;
  border-radius: var(--ex-radius-panel-large);
  background: var(--ex-color-dark);
}

.home-sample-head {
  display: flex;
  align-items: center;
  gap: 28px;
}

.home-sample-head figcaption {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.home-sample-label {
  font-size: 13px;
  color: var(--ex-color-neutral-300);
}

.home-sample-page {
  font-family: var(--ex-font-heading);
  font-size: 22px;
  font-weight: 600;
  color: var(--ex-color-white);
}

.home-sample-bars {
  margin: 24px 0 0;
  padding: 0;
  list-style: none;
}

.home-sample-bars li {
  display: grid;
  grid-template-columns: 28px 140px minmax(0, 1fr) 64px;
  gap: 12px;
  align-items: center;
  padding: 10px 0;
}

.home-sample-icon {
  color: var(--ex-color-neutral-100);
}

.home-sample-name {
  font-size: 15px;
  font-weight: 600;
  color: var(--ex-color-white);
}

.home-sample-track {
  height: 8px;
  border-radius: var(--ex-radius-pill);
  background: var(--ex-color-on-dark-subtle);
}

.home-sample-track span {
  display: block;
  height: 8px;
  border-radius: var(--ex-radius-pill);
}

.home-sample-score {
  text-align: right;
  font-family: var(--ex-font-heading);
  font-size: 16px;
  font-weight: 600;
  color: var(--ex-color-white);
}

.home-sample-score span {
  font-size: 12px;
  font-weight: 500;
  color: var(--ex-color-neutral-500);
}

.home-checks {
  display: grid;
  grid-template-columns: 420px 1fr;
  gap: 64px;
  background: var(--ex-color-surface-blue);
}

.home-category-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.home-category {
  padding: 22px;
  border: 1px solid var(--ex-color-hairline);
  border-radius: var(--ex-radius-tile);
  background: var(--ex-color-white);
}

.home-category-icon {
  color: var(--ex-color-blue);
}

.home-category h3 {
  margin-top: 14px;
  font-size: 18px;
  letter-spacing: -0.02em;
}

.home-category p {
  margin-top: 6px;
  font-size: 14px;
  line-height: 1.5;
  color: var(--ex-color-neutral-700);
}

.home-reality {
  background: var(--ex-color-white);
}

.home-reality-head {
  text-align: center;
}

.home-reality-cards {
  position: relative;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  margin-top: 56px;
}

/* Points from what the code says to what visitors do, across the gap between the cards. */
.home-reality-bridge {
  position: absolute;
  top: 50%;
  left: 50%;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border: 1px solid var(--ex-color-hairline);
  border-radius: var(--ex-radius-pill);
  background: var(--ex-color-white);
  color: var(--ex-color-blue);
  transform: translate(-50%, -50%);
}

.home-code-card {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.home-code-text {
  margin-top: 10px;
  font-size: 15px;
  line-height: 1.55;
  color: var(--ex-color-neutral-700);
}

.home-code-text strong {
  font-weight: 600;
  color: var(--ex-color-dark);
}

.home-code-card .home-section-action {
  margin-top: auto;
}

.home-code-card,
.home-visitors-card {
  padding: 36px;
  border-radius: var(--ex-radius-panel-large);
}

.home-code-card {
  border: 1px solid var(--ex-color-hairline);
}

.home-visitors-card {
  background: var(--ex-color-dark);
  color: var(--ex-color-white);
}

.home-card-eyebrow {
  font-size: 13px;
  font-weight: 600;
  color: var(--ex-color-blue);
}

.home-card-eyebrow--mint {
  color: var(--ex-color-mint);
}

.home-card-title {
  margin-top: 10px;
  font-size: 28px;
  letter-spacing: -0.02em;
}

.home-visitors-card .home-card-title {
  color: var(--ex-color-white);
}

.home-code-list {
  align-self: stretch;
  margin: 20px 0 28px;
  padding: 0;
  list-style: none;
}

.home-code-list li {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 0;
  border-top: 1px solid var(--ex-color-hairline);
  font-size: 15px;
}

.home-code-list li span:first-of-type {
  flex: 1;
}

.home-code-icon {
  color: var(--ex-color-dark);
}

.home-code-category {
  font-size: 13px;
  color: var(--ex-color-neutral-500);
}

.home-visitor-figures {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  margin: 22px 0 0;
}

.home-visitor-figures div {
  padding: 16px;
  border-radius: var(--ex-radius-tile);
  background: var(--ex-color-on-dark-faint);
}

.home-visitor-figures dt {
  font-size: 12px;
  color: var(--ex-color-neutral-300);
}

.home-visitor-figures dd {
  margin: 6px 0 0;
  font-family: var(--ex-font-heading);
  font-size: 26px;
  font-weight: 600;
  color: var(--ex-color-white);
}

.home-visitor-figures .home-visitor-figure--mint {
  color: var(--ex-color-mint);
}

.home-benefits {
  margin: 18px 0 0;
  padding: 0;
  list-style: none;
}

.home-benefits li {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  padding: 5px 0;
  font-size: 14px;
  line-height: 1.5;
}

.home-benefits .ph-icon {
  margin-top: 3px;
  color: var(--ex-color-mint);
}

.home-visitors-actions {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-top: 22px;
  font-size: 13px;
  color: var(--ex-color-neutral-300);
}

.home-chrome {
  padding-top: 0;
}

.home-faq {
  display: grid;
  grid-template-columns: 1fr 1.4fr;
  gap: 64px;
  padding-top: 24px;
}

.home-faq-list {
  margin: 0;
  padding: 0;
  list-style: none;
}

.home-faq-list li {
  border-top: 1px solid var(--ex-color-hairline);
}

.home-faq-list h3 {
  font-family: var(--ex-font-body);
  letter-spacing: normal;
}

.home-faq-toggle {
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;
  padding: 20px 0;
  border: 0;
  background: none;
  color: var(--ex-color-dark);
  font: inherit;
  font-size: 17px;
  font-weight: 600;
  text-align: left;
  cursor: pointer;
}

.home-faq-toggle .ph-icon {
  margin-left: auto;
  color: var(--ex-color-neutral-500);
}

.home-faq-answer {
  max-width: 640px;
  padding-bottom: 20px;
  font-size: 16px;
  line-height: 1.55;
  color: var(--ex-color-neutral-700);
}

.home-final {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 88px var(--ex-page-gutter);
  background: var(--ex-color-dark);
  text-align: center;
}

.home-final-title {
  font-size: 52px;
  letter-spacing: -0.035em;
  line-height: 1.08;
  color: var(--ex-color-white);
}

@media (max-width: 960px) {
  .home-hero,
  .home-section,
  .home-final {
    padding: 56px 16px;
  }

  .home-title {
    font-size: 44px;
  }

  .home-intro {
    font-size: 17px;
  }

  .home-heading,
  .home-heading--small,
  .home-heading--faq,
  .home-final-title {
    font-size: 34px;
  }

  .home-reality-bridge {
    display: none;
  }

  .home-report,
  .home-checks,
  .home-reality-cards,
  .home-faq {
    grid-template-columns: 1fr;
    gap: 32px;
  }

  .home-chrome {
    padding-top: 0;
  }

  .home-sample,
  .home-code-card,
  .home-visitors-card {
    padding: 24px;
  }

  .home-sample-bars li {
    grid-template-columns: 24px 110px minmax(0, 1fr) 52px;
    gap: 8px;
  }
}

@media (max-width: 640px) {
  .home-title br,
  .home-heading br,
  .home-final-title br {
    display: none;
  }

  .home-category-grid,
  .home-visitor-figures {
    grid-template-columns: 1fr;
  }

  .home-sample-head {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
